import { useCallback, useState } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import { getVerifastApplicantLogs } from "@/lib/api/verifastService";
import { showApiError } from "@/lib/utils/notifications";
import { VERIFAST_LOGS_VIEWER_ROLES } from "../constants";
import type { VerifastSessionGroup } from "../types/utils";
import {
  buildVerifastSessionGroups,
  hasAnyVerifastEventRows,
} from "../utils/verifastLogsMapper";

export function useVerifastLogs(options: {
  applicantId: string;
  hasVerifastSteps?: boolean;
}) {
  const { applicantId, hasVerifastSteps = false } = options;
  const { roleName } = useUserRole();

  const canViewLogs = VERIFAST_LOGS_VIEWER_ROLES.includes(roleName);
  const showViewLogsButton = canViewLogs && Boolean(hasVerifastSteps);

  const [logsLoading, setLogsLoading] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logSessions, setLogSessions] = useState<VerifastSessionGroup[]>([]);
  const [emptyLogsDialogOpen, setEmptyLogsDialogOpen] = useState(false);

  const openVerifastLogs = useCallback(async () => {
    if (!canViewLogs || !hasVerifastSteps) return;
    if (!applicantId || logsLoading) return;

    setLogsLoading(true);
    try {
      const data = await getVerifastApplicantLogs(applicantId);
      const groups = buildVerifastSessionGroups(data);
      if (!hasAnyVerifastEventRows(groups)) {
        setEmptyLogsDialogOpen(true);
        return;
      }
      setLogSessions(groups);
      setLogsModalOpen(true);
    } catch (error: unknown) {
      showApiError(error);
    } finally {
      setLogsLoading(false);
    }
  }, [applicantId, canViewLogs, hasVerifastSteps, logsLoading]);

  const closeLogsModal = useCallback(() => {
    setLogsModalOpen(false);
  }, []);

  return {
    showViewLogsButton,
    logsLoading,
    openVerifastLogs,
    logsModalOpen,
    closeLogsModal,
    logSessions,
    emptyLogsDialogOpen,
    setEmptyLogsDialogOpen,
  };
}
